import { Link, useNavigate } from "react-router-dom";
import { authUrl } from "@/lib/authRedirect";
import { CATEGORIES } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import CartIcon from "./CartIcon";
import RentifyLogo from "./RentifyLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent } from
"@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger } from
"@/components/ui/sheet";
import {
  User,
  LogOut,
  Menu,
  Bell,
  MessageCircle,
  Heart,
  Calendar,
  Package,
  Settings,
  Languages,
  Search,
  Home,
  LayoutDashboard,
  History,
  Shield,
  Plus,
  MapPin,
  ChevronDown,
  LayoutGrid,
  ClipboardList } from
"lucide-react";
import USCitySelect from "./USCitySelect";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWishlist } from "@/contexts/WishlistContext";
import { useIsMobile } from "@/hooks/use-mobile";


const cap = (v: string) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : v);

const Header = () => {
  const { user, signOut } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [city, setCity] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("rentify_city") || "";
  });

  useEffect(() => {
    if (city) localStorage.setItem("rentify_city", city);
  }, [city]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("search", searchQuery.trim());
    if (city) params.set("city", city);
    navigate(`/browse${params.toString() ? `?${params.toString()}` : ""}`);
    setSearchQuery("");
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    supabase.from("user_roles").select("role")
      .eq("user_id", user.id).eq("role", "admin").maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [user]);
  const { getWishlistCount } = useWishlist();
  const isMobile = useIsMobile();

  const fetchUnreadCounts = useCallback(async () => {
    if (!user) {
      setUnreadNotifications(0);
      setUnreadMessages(0);
      return;
    }

    try {
      // Fetch unread notifications
      const { count: notifCount, error: notifError } = await supabase.
      from("notifications").
      select("*", { count: "exact", head: true }).
      eq("user_id", user.id).
      eq("read", false);

      if (notifError) throw notifError;
      setUnreadNotifications(notifCount || 0);

      // Fetch unread messages - get bookings where user is renter
      const { data: renterBookings, error: renterBookingsError } = await supabase.
      from("bookings").
      select("id").
      eq("renter_id", user.id);

      if (renterBookingsError) throw renterBookingsError;

      // Also get bookings where user is item owner
      const { data: ownerItems, error: ownerItemsError } = await supabase.
      from("items").
      select("id").
      eq("owner_id", user.id);

      if (ownerItemsError) throw ownerItemsError;

      let ownerBookings: {id: string;}[] = [];
      if (ownerItems && ownerItems.length > 0) {
        const { data: ob, error: ownerBookingsError } = await supabase.
        from("bookings").
        select("id").
        in("item_id", ownerItems.map((i) => i.id));
        if (ownerBookingsError) throw ownerBookingsError;
        ownerBookings = ob || [];
      }

      const bookings = [...(renterBookings || []), ...ownerBookings];

      if (bookings && bookings.length > 0) {
        const bookingIds = bookings.map((b) => b.id);
        const { count: msgCount, error: msgCountError } = await supabase.
        from("messages").
        select("*", { count: "exact", head: true }).
        in("booking_id", bookingIds).
        neq("sender_id", user.id).
        is("read_at", null);

        if (msgCountError) throw msgCountError;
        setUnreadMessages(msgCount || 0);
      } else {
        setUnreadMessages(0);
      }
    } catch (error) {
      console.error("Error fetching header counts:", error);
      setUnreadNotifications(0);
      setUnreadMessages(0);
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  // Realtime subscription for notification count updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('header-notification-count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchUnreadCounts();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        },
        () => {
          fetchUnreadCounts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnreadCounts]);

  const handleNavClick = (to: string) => {
    setMobileMenuOpen(false);
    navigate(to);
  };

  const totalBadgeCount = unreadNotifications + unreadMessages + getWishlistCount();

  const languages = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" }];


  return (
    <header className="border-b border-border/50 bg-background sticky top-0 z-50 backdrop-blur-sm bg-background/95">
      <div className="container px-4 md:px-6 h-16 md:h-18 flex items-center justify-between">
        {/* Left: Logo */}
        <div className="flex items-center gap-2">
          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="mr-2" aria-label={mobileMenuOpen ? "Close menu" : "Open menu"} aria-expanded={mobileMenuOpen}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetHeader>
                <SheetTitle className="sr-only">Main menu</SheetTitle>
                <SheetDescription className="sr-only">Navigate Rentify, search items, and manage your account.</SheetDescription>
                <div className="text-left" aria-hidden="true">
                  <RentifyLogo size="md" linkToHome={false} />
                </div>
              </SheetHeader>
              <nav className="flex flex-col gap-2 mt-6">
                {/* Mobile search bar */}
                <div className="relative px-2 mb-2">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    type="search"
                    placeholder="Search items..."
                    className="w-full h-10 pl-10 pr-4 bg-white border border-gray-200/80 rounded-md text-sm shadow-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-primary"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button
                  variant="ghost"
                  className="justify-start text-base capitalize"
                  onClick={() => handleNavClick("/browse")}>

                  <Search className="w-4 h-4 mr-2" />
                   {cap(t("browse"))}
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-base"
                  onClick={() => handleNavClick("/")}>
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-base"
                  onClick={() => handleNavClick("/list-item")}>
                  <Plus className="w-4 h-4 mr-2" />
                  List Items
                </Button>
                {user ?
                <>
                    <div className="h-px bg-border my-2" />
                    <Button
                    variant="ghost"
                    className="justify-start text-base capitalize"
                    onClick={() => handleNavClick("/notifications")}>

                      <Bell className="w-4 h-4 mr-2" />
                      {cap(t("notifications"))}
                      {unreadNotifications > 0 &&
                    <span className="ml-auto bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                          {unreadNotifications}
                        </span>
                    }
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base capitalize"
                    onClick={() => handleNavClick("/messages")}>

                      <MessageCircle className="w-4 h-4 mr-2" />
                      {cap(t("messages") || "Messages")}
                      {unreadMessages > 0 &&
                    <span className="ml-auto bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                          {unreadMessages}
                        </span>
                    }
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base capitalize"
                    onClick={() => handleNavClick("/wishlist")}>

                      <Heart className="w-4 h-4 mr-2" />
                      {cap(t("wishlist") || "Wishlist")}
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base capitalize"
                    onClick={() => handleNavClick("/edit-profile")}>

                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base capitalize"
                    onClick={() => handleNavClick("/bookings")}>

                      <Calendar className="w-4 h-4 mr-2" />
                      {cap(t("myBookings"))}
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleNavClick("/my-listings")}>

                      <Package className="w-4 h-4 mr-2" />
                      My Listings
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleNavClick("/rental-history")}>

                      <History className="w-4 h-4 mr-2" />
                      Rental History
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleNavClick("/my-requests")}>

                      <Package className="w-4 h-4 mr-2" />
                      My Requests
                    </Button>
                    <Button
                    variant="ghost"
                    className="justify-start text-base capitalize"
                    onClick={() => handleNavClick("/settings")}>

                      <Settings className="w-4 h-4 mr-2" />
                      Account settings
                    </Button>
                    <div className="h-px bg-border my-2" />
                    <Button
                    variant="ghost"
                    className="justify-start text-base text-destructive"
                    onClick={async () => {
                      setMobileMenuOpen(false);
                      try {
                        await signOut();
                      } catch (e) {
                        console.error("Sign out failed:", e);
                      }
                    }}>

                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </Button>

                  </> :

                <>
                    <div className="h-px bg-border my-2" />
                    <Button
                    variant="default"
                    className="justify-center"
                    onClick={() => handleNavClick("/auth")}>

                      {cap(t("login"))}
                    </Button>
                  </>
                }
              </nav>
            </SheetContent>
          </Sheet>
          
          <RentifyLogo size="md" mobile={isMobile} />
        </div>
        
        {/* Center: City + Search + Categories */}
        <div className="hidden md:flex items-center gap-2 lg:gap-3 flex-1 justify-center px-4">
          {/* City selector */}
          <div className="hidden lg:block w-52 shrink-0">
            <USCitySelect
              value={city}
              onChange={setCity}
              placeholder="Select city"
              className="[&_input]:h-11 [&_input]:rounded-md [&_input]:bg-white [&_input]:border-gray-200 [&_input]:shadow-sm [&_input]:pl-10"
            />
          </div>

          {/* Header search bar */}
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="Search items"
              className="w-full h-11 pl-11 pr-4 bg-white border border-gray-200 rounded-md text-sm shadow-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary/50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          {/* Browse Categories dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-11 rounded-md bg-white border-gray-200 shadow-sm gap-2 px-4 shrink-0 hidden lg:inline-flex">
                <LayoutGrid className="h-4 w-4" />
                <span className="font-medium">Browse Categories</span>
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background z-50">
              <DropdownMenuItem onClick={() => navigate("/browse")}>All Categories</DropdownMenuItem>
              <DropdownMenuSeparator />
              {CATEGORIES.map((c) => (
                <DropdownMenuItem key={c} onClick={() => navigate(`/browse?category=${encodeURIComponent(c)}`)}>
                  {c}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Right: List an item + Messages + Orders + Wishlist + Profile */}
        <div className="flex items-center gap-1 md:gap-2">
          <Button
            onClick={() => navigate("/list-item")}
            className="hidden md:inline-flex h-11 rounded-md bg-[#efe6f9] hover:bg-[#e5d5f5] text-slate-900 font-medium px-4 gap-2 shadow-sm border border-transparent">
            <Plus className="h-4 w-4" />
            List an item
          </Button>

          <Link
            to={user ? "/messages" : authUrl(undefined, "/messages")}
            className="hidden md:inline-flex items-center gap-2 h-11 px-3 rounded-md hover:bg-accent transition-colors relative"
            aria-label="Messages">
            <MessageCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Messages</span>
            {user && unreadMessages > 0 && (
              <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </span>
            )}
          </Link>

          <Link
            to={user ? "/bookings" : authUrl(undefined, "/bookings")}
            className="hidden md:inline-flex items-center gap-2 h-11 px-3 rounded-md hover:bg-accent transition-colors"
            aria-label="Orders">
            <ClipboardList className="h-5 w-5" />
            <span className="text-sm font-medium">Orders</span>
          </Link>

          <Link
            to="/wishlist"
            className="hidden md:inline-flex p-2 hover:bg-accent rounded-md transition-colors"
            aria-label="Wishlist">
            <Heart className="h-5 w-5" />
          </Link>

          {user &&
          <Link to="/notifications" className="relative p-2 hover:bg-accent rounded-md transition-colors md:hidden" aria-label="Notifications">
              <Bell className="h-5 w-5" />
              {unreadNotifications > 0 &&
            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </span>
            }
            </Link>
          }
          <CartIcon />

          {user &&
          <Link to="/dashboard" className="relative p-2 hover:bg-accent rounded-md transition-colors" aria-label="Dashboard">
              <LayoutDashboard className="h-5 w-5" />
            </Link>
          }
          
          {user ?
          <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" aria-label="User account menu">
                  <User className="h-5 w-5" />
                  {totalBadgeCount > 0 &&
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {totalBadgeCount > 9 ? "9+" : totalBadgeCount}
                    </span>
                }
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                <DropdownMenuLabel className="capitalize">{cap(t("account"))}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={() => navigate("/edit-profile")}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate("/notifications")} className="flex justify-between capitalize">
                  <span className="flex items-center">
                    <Bell className="mr-2 h-4 w-4" />
                    {cap(t("notifications"))}
                  </span>
                  {unreadNotifications > 0 &&
                <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                      {unreadNotifications}
                    </span>
                }
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate("/messages")} className="flex justify-between capitalize">
                  <span className="flex items-center">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    {cap(t("messages") || "Messages")}
                  </span>
                  {unreadMessages > 0 &&
                <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                      {unreadMessages}
                    </span>
                }
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate("/wishlist")} className="flex justify-between capitalize">
                  <span className="flex items-center">
                    <Heart className="mr-2 h-4 w-4" />
                    {cap(t("wishlist") || "Wishlist")}
                  </span>
                  {getWishlistCount() > 0 &&
                <span className="bg-muted text-muted-foreground text-xs rounded-full px-2 py-0.5">
                      {getWishlistCount()}
                    </span>
                }
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Dashboard
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={() => navigate("/bookings")} className="capitalize">
                  <Calendar className="mr-2 h-4 w-4" />
                  {cap(t("myBookings"))}
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate("/my-listings")}>
                  <Package className="mr-2 h-4 w-4" />
                  My Listings
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate("/rental-history")}>
                  <History className="mr-2 h-4 w-4" />
                  Rental History
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => navigate("/my-requests")}>
                  <Package className="mr-2 h-4 w-4" />
                  My Requests
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={() => navigate("/settings")} className="capitalize">
                  <Settings className="mr-2 h-4 w-4" />
                  Account settings
                </DropdownMenuItem>

                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Shield className="mr-2 h-4 w-4" />
                    Admin Console
                  </DropdownMenuItem>
                )}
                
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="capitalize">
                    <Languages className="mr-2 h-4 w-4" />
                    Language preferences
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="bg-background">
                    {languages.map((lang) =>
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code as any)}
                    className={language === lang.code ? "bg-accent" : ""}>

                        {lang.flag} {lang.label}
                      </DropdownMenuItem>
                  )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive capitalize">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu> :

          <Button
            variant="default"
            size="sm"
            className="font-medium"
            onClick={() => navigate(authUrl())}>

              {cap(t("login"))}
            </Button>
          }
        </div>
      </div>
    </header>);

};

export default Header;